import { useCallback, useEffect, useState } from 'react';

/**
 * V2 Voice GM (2026-06-02) — mic activation mode persistence.
 *
 * Mirrors vibe-designer's `settings.activation` pattern (toggle vs
 * push-to-talk) — see vibe-designer/frontend/src/components/voice/
 * VoiceControlsBar.tsx:127. The user picked "both" in the V2 design
 * question; we expose it as a user-pick preference like vibe-designer
 * does, default 'toggle' to match vibe-designer's default at L129.
 */

export type MicActivation = 'toggle' | 'ptt';
const STORAGE_KEY = 'briefing-room:mic-activation';
export const MIC_ACTIVATION_DEFAULT: MicActivation = 'toggle';

function readStored(): MicActivation {
  if (typeof window === 'undefined') {
    return MIC_ACTIVATION_DEFAULT;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'ptt' || stored === 'toggle') {
      return stored;
    }
  } catch {
    // degrade quietly
  }
  return MIC_ACTIVATION_DEFAULT;
}

function writeStored(value: MicActivation): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // degrade quietly
  }
}

export function useMicActivation(): [MicActivation, (next: MicActivation) => void] {
  const [activation, setActivationState] = useState<MicActivation>(readStored);

  useEffect(() => {
    const handler = (e: Event) => {
      if (e instanceof CustomEvent && (e.detail === 'ptt' || e.detail === 'toggle')) {
        setActivationState(e.detail);
        return;
      }
      setActivationState(readStored());
    };
    window.addEventListener('briefing-room:mic-activation-changed', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('briefing-room:mic-activation-changed', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  const setActivation = useCallback((next: MicActivation) => {
    setActivationState(next);
    writeStored(next);
    window.dispatchEvent(
      new CustomEvent('briefing-room:mic-activation-changed', { detail: next }),
    );
  }, []);

  return [activation, setActivation];
}
